import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import ReactMarkdown from 'react-markdown';
import { v4 as uuidv4 } from 'uuid';
import { TaskData } from './TaskData';
import PersonAddIcon from '@mui/icons-material/PersonAdd'; // 追加アイコン

interface UserData {
  id: string;
  name: string;
  email: string;
  passwordHash: string;
}

interface MarkdownData {
  id: string;
  title: string;
  content: string;
}

const MarkdownDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<MarkdownData | null>(null);
  const [steps, setSteps] = useState<string[]>([]);
  const [userList, setUserList] = useState<UserData[]>([]);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      const saved = localStorage.getItem('markdown-list');
      if (saved) {
        const list: MarkdownData[] = JSON.parse(saved);
        const found = list.find((d) => d.id === id);
        if (found) {
          setDoc(found);
          extractSteps(found.content);
        }
      }
    }
  }, [id]);

  const extractSteps = (content: string) => {
    const lines = content.split('\n');
    const stepsList: string[] = [];
    lines.forEach((line) => {
      if (line.startsWith('##')) {
        stepsList.push(line.replace(/^##\s*/, '').trim());
      }
    });
    setSteps(stepsList);
  };

  const loadUserList = () => {
    const saved = localStorage.getItem('user-list');
    if (saved) {
      setUserList(JSON.parse(saved));
    }
  };

  const openUserDialog = () => {
    loadUserList();
    setIsUserDialogOpen(true);
  };

  const handleCreateTask = (userId: string) => {
    if (!doc) return;

    const savedTasks = localStorage.getItem('task-list');
    const currentTasks: TaskData[] = savedTasks ? JSON.parse(savedTasks) : [];

    const newTask: TaskData = {
      id: uuidv4(),
      markdownId: doc.id,
      userId,
      status: '未着手',
      createdAt: new Date().toISOString(),
    };

    const updatedTasks = [...currentTasks, newTask];
    localStorage.setItem('task-list', JSON.stringify(updatedTasks));
    alert('タスクを作成しました');
    setIsUserDialogOpen(false);
  };

  if (!doc) {
    return <Typography>読み込み中...</Typography>;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {/* タイトルとアクションボタン */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{doc.title}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={openUserDialog}
        >
          タスク作成
        </Button>
      </Box>

      {/* 詳細 */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>詳細</Typography>
        <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 2 }}>
          <ReactMarkdown>{doc.content}</ReactMarkdown>
        </Box>
      </Paper>

      {/* ステップ一覧 */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>ステップ一覧</Typography>
        {steps.length > 0 ? (
          <Timeline>
            {steps.map((step, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot color="primary" />
                  {index < steps.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    {step}
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        ) : (
          <Typography color="text.secondary">ステップは定義されていません。</Typography>
        )}
      </Paper>

      {/* ユーザー選択ダイアログ */}
      <Dialog open={isUserDialogOpen} onClose={() => setIsUserDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>タスクを作成するユーザーを選んでください</DialogTitle>
        <DialogContent dividers>
          <List>
            {userList.map((user) => (
              <ListItem key={user.id} disablePadding divider>
                <ListItemButton onClick={() => handleCreateTask(user.id)}>
                  <ListItemText
                    primary={user.name}
                    secondary={user.email}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsUserDialogOpen(false)}>キャンセル</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MarkdownDetail;
